import type {
  InteractionAction,
  InteractionActionType,
  InteractionTrigger,
  WebKilnProject,
} from '../types';
import {
  createInteraction,
  INTERACTION_ACTIONS,
  INTERACTION_TRIGGERS,
} from '../models/interaction-schema';
import { WebKilnEditorAdapter } from './webkiln-editor-adapter';
import {
  installInteractionRuntime,
  resetInteractions,
  runInteraction,
} from './interaction-runtime';

export class InteractionEditorController {
  private previewCleanup: (() => void) | null = null;
  constructor(
    private readonly adapter: WebKilnEditorAdapter,
    private readonly project: WebKilnProject,
    private readonly dirty: () => void,
  ) { }

  start(): void {
    const panel = document.querySelector<HTMLElement>('#sitePanel');
    if (!panel || panel.querySelector('[data-interaction-editor]')) return;
    const card = document.createElement('section');
    card.dataset.interactionEditor = 'true';
    card.className = 'recovery-card interaction-editor';
    card.innerHTML = `<div class="interaction-heading"><div><p class="eyebrow">Interactions</p><strong>Motion with intent</strong><small>Build safe, reusable behaviors without inline JavaScript.</small></div><div><button type="button" class="ghost-btn" data-preview-interactions>Preview</button><button type="button" class="ghost-btn" data-pause-interactions>Pause</button><button type="button" class="ghost-btn" data-reset-interactions>Reset</button><button type="button" class="primary-btn" data-add-interaction>Add interaction</button></div></div><div data-interaction-list></div><p class="field-help">Reduced-motion users receive instant or skipped motion according to each action's fallback.</p>`;
    panel.prepend(card);
    card.querySelector('[data-add-interaction]')?.addEventListener('click', () => {
      const id = this.adapter.getComponentId(this.adapter.getSelectedComponent());
      if (!id) {
        this.showMessage(card, 'Select an element on the canvas first.');
        return;
      }
      const interaction = createInteraction(`[data-wk-id="${id}"]`);
      this.project.editorSettings!.interactions = [
        ...(this.project.editorSettings?.interactions ?? []),
        interaction,
      ];
      this.ensureTargetAttribute(id);
      this.dirty();
      this.render(card);
    });
    card.querySelector('[data-preview-interactions]')?.addEventListener('click', () => {
      this.previewCleanup?.();
      const frame = this.adapter.getFrameDocument();
      if (frame) {
        resetInteractions(frame);
        this.previewCleanup = installInteractionRuntime(
          frame,
          this.project.editorSettings?.interactions,
        );
        const interactions = this.project.editorSettings?.interactions ?? [];
        window.setTimeout(() => {
          interactions
            .filter((i) => i.enabled)
            .forEach((item) => runInteraction(frame, item));
        }, 30);
      }
      this.showMessage(card, 'Preview running. Click element to toggle animation.');
    });
    card.querySelector('[data-pause-interactions]')?.addEventListener('click', () => {
      this.previewCleanup?.();
      this.previewCleanup = null;
      this.showMessage(card, 'Preview paused.');
    });
    card.querySelector('[data-reset-interactions]')?.addEventListener('click', () => {
      this.previewCleanup?.();
      this.previewCleanup = null;
      const frame = this.adapter.getFrameDocument();
      if (frame) resetInteractions(frame);
      this.dirty();
      this.render(card);
      this.showMessage(card, 'Preview reset to default state.');
    });
    this.render(card);
  }

  private ensureTargetAttribute(id: string): void {
    const selected = this.adapter.getSelectedComponent();
    if (selected) {
      this.adapter.updateComponentTraits(selected, { 'data-wk-id': id });
      const el = (selected as { getEl?: () => HTMLElement | null }).getEl?.();
      if (el) {
        el.setAttribute('data-wk-id', id);
        el.dataset.wkId = id;
      }
    }
  }

  private render(card: HTMLElement): void {
    const list = card.querySelector<HTMLElement>('[data-interaction-list]');
    if (!list) return;
    const interactions = this.project.editorSettings?.interactions ?? [];
    list.innerHTML = interactions.length
      ? interactions
        .map(
          (item, index) =>
            `<article class="interaction-row" data-interaction-index="${index}"><div class="interaction-flow"><span>${this.label(INTERACTION_TRIGGERS, item.trigger)}</span><b>→</b><span>${item.actions.length} action${item.actions.length === 1 ? '' : 's'}</span></div><div class="interaction-controls"><label>Name<input data-interaction-name="${index}" value="${this.escape(item.name)}" /></label><label>Trigger<select data-interaction-trigger="${index}">${INTERACTION_TRIGGERS.map((trigger) => `<option value="${trigger.id}" ${trigger.id === item.trigger ? 'selected' : ''}>${trigger.label}</option>`).join('')}</select></label><label>Sequence<select data-interaction-sequence="${index}"><option value="sequence" ${item.sequence === 'sequence' ? 'selected' : ''}>Sequence (ordered)</option><option value="parallel" ${item.sequence === 'parallel' ? 'selected' : ''}>Parallel (simultaneous)</option></select></label><label>Action<select data-interaction-action="${index}">${INTERACTION_ACTIONS.map((action) => `<option value="${action.id}" ${action.id === item.actions[0]?.type ? 'selected' : ''}>${action.label}</option>`).join('')}</select></label><label>Value<input data-interaction-value="${index}" value="${this.escape(String(item.actions[0]?.value ?? ''))}" placeholder="ex: 15deg, 40px, 0.2" /></label><label>Duration (ms)<input type="number" min="0" max="10000" step="50" data-interaction-duration="${index}" value="${item.actions[0]?.duration ?? 300}" /></label><label>Delay (ms)<input type="number" min="0" max="10000" step="50" data-interaction-delay="${index}" value="${item.actions[0]?.delay ?? 0}" /></label><label>Easing<select data-interaction-easing="${index}"><option value="ease" ${item.actions[0]?.easing === 'ease' ? 'selected' : ''}>Ease</option><option value="linear" ${item.actions[0]?.easing === 'linear' ? 'selected' : ''}>Linear</option><option value="ease-in" ${item.actions[0]?.easing === 'ease-in' ? 'selected' : ''}>Ease In</option><option value="ease-out" ${item.actions[0]?.easing === 'ease-out' ? 'selected' : ''}>Ease Out</option><option value="ease-in-out" ${item.actions[0]?.easing === 'ease-in-out' ? 'selected' : ''}>Ease In Out</option></select></label><label>Reduced motion<select data-interaction-reduced-motion="${index}"><option value="instant" ${item.actions[0]?.reducedMotion === 'instant' ? 'selected' : ''}>Instant</option><option value="skip" ${item.actions[0]?.reducedMotion === 'skip' ? 'selected' : ''}>Skip</option><option value="preserve" ${item.actions[0]?.reducedMotion === 'preserve' ? 'selected' : ''}>Preserve</option></select></label><label class="switch-field"><input type="checkbox" data-interaction-loop="${index}" ${item.loop?.enabled ? 'checked' : ''} /> Loop animation</label><label class="switch-field"><input type="checkbox" data-interaction-enabled="${index}" ${item.enabled ? 'checked' : ''} /> Enabled</label><button type="button" class="text-button" data-duplicate-interaction="${index}">Duplicate</button><button type="button" class="text-button danger-action" data-delete-interaction="${index}">Remove</button></div></article>`,
        )
        .join('')
      : '<small>No interactions yet. Select a canvas element and add one.</small>';
    list
      .querySelectorAll<HTMLInputElement>('[data-interaction-name]')
      .forEach((input) =>
        input.addEventListener('change', () =>
          this.update(interactions, Number(input.dataset.interactionName), { name: input.value }),
        ),
      );
    list.querySelectorAll<HTMLSelectElement>('[data-interaction-trigger]').forEach((input) =>
      input.addEventListener('change', () =>
        this.update(interactions, Number(input.dataset.interactionTrigger), {
          trigger: input.value as InteractionTrigger,
        }),
      ),
    );
    list.querySelectorAll<HTMLSelectElement>('[data-interaction-sequence]').forEach((input) =>
      input.addEventListener('change', () =>
        this.update(interactions, Number(input.dataset.interactionSequence), {
          sequence: input.value as 'sequence' | 'parallel',
        }),
      ),
    );
    list.querySelectorAll<HTMLSelectElement>('[data-interaction-action]').forEach((input) =>
      input.addEventListener('change', () =>
        this.updateAction(interactions, Number(input.dataset.interactionAction), {
          type: input.value as InteractionActionType,
        }),
      ),
    );
    list.querySelectorAll<HTMLInputElement>('[data-interaction-value]').forEach((input) => {
      const handler = () =>
        this.updateAction(interactions, Number(input.dataset.interactionValue), {
          value: input.value,
        });
      input.addEventListener('input', handler);
      input.addEventListener('change', handler);
    });
    list.querySelectorAll<HTMLInputElement>('[data-interaction-duration]').forEach((input) =>
      input.addEventListener('change', () =>
        this.updateAction(interactions, Number(input.dataset.interactionDuration), {
          duration: Number(input.value),
        }),
      ),
    );
    list.querySelectorAll<HTMLInputElement>('[data-interaction-delay]').forEach((input) =>
      input.addEventListener('change', () =>
        this.updateAction(interactions, Number(input.dataset.interactionDelay), {
          delay: Number(input.value),
        }),
      ),
    );
    list.querySelectorAll<HTMLSelectElement>('[data-interaction-easing]').forEach((input) =>
      input.addEventListener('change', () =>
        this.updateAction(interactions, Number(input.dataset.interactionEasing), {
          easing: input.value as 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out',
        }),
      ),
    );
    list.querySelectorAll<HTMLSelectElement>('[data-interaction-reduced-motion]').forEach((input) =>
      input.addEventListener('change', () =>
        this.updateAction(interactions, Number(input.dataset.interactionReducedMotion), {
          reducedMotion: input.value as 'instant' | 'skip' | 'preserve',
        }),
      ),
    );
    list.querySelectorAll<HTMLInputElement>('[data-interaction-loop]').forEach((input) =>
      input.addEventListener('change', () => {
        const item = (interactions ?? [])[Number(input.dataset.interactionLoop)];
        if (item) {
          item.loop = {
            enabled: input.checked,
            count: item.loop?.count ?? 2,
            delay: item.loop?.delay ?? 500,
          };
          this.dirty();
        }
      }),
    );
    list.querySelectorAll<HTMLInputElement>('[data-interaction-enabled]').forEach((input) =>
      input.addEventListener('change', () =>
        this.update(interactions, Number(input.dataset.interactionEnabled), {
          enabled: input.checked,
        }),
      ),
    );
    list.querySelectorAll<HTMLButtonElement>('[data-delete-interaction]').forEach((button) =>
      button.addEventListener('click', () => {
        interactions.splice(Number(button.dataset.deleteInteraction), 1);
        this.dirty();
        this.render(card);
      }),
    );
    list.querySelectorAll<HTMLButtonElement>('[data-duplicate-interaction]').forEach((button) =>
      button.addEventListener('click', () => {
        const index = Number(button.dataset.duplicateInteraction);
        const source = interactions[index];
        if (!source) return;
        const copy = structuredClone(source);
        copy.id = crypto.randomUUID();
        copy.name = `${copy.name} copy`;
        copy.actions = copy.actions.map((action) => ({ ...action, id: crypto.randomUUID() }));
        interactions.splice(index + 1, 0, copy);
        this.dirty();
        this.render(card);
      }),
    );
  }

  private update(
    interactions: NonNullable<WebKilnProject['editorSettings']>['interactions'],
    index: number,
    value: Record<string, unknown>,
  ): void {
    const item = interactions?.[index];
    if (!item) return;
    Object.assign(item, value);
    this.dirty();
  }

  private updateAction(
    interactions: NonNullable<WebKilnProject['editorSettings']>['interactions'],
    index: number,
    patch: Partial<InteractionAction>,
  ): void {
    const action = interactions?.[index]?.actions[0];
    if (!action) return;
    if (patch.type) action.type = patch.type;
    if (patch.duration !== undefined)
      action.duration = Math.min(10000, Math.max(0, patch.duration));
    if (patch.delay !== undefined) action.delay = Math.min(10000, Math.max(0, patch.delay));
    if (patch.easing) action.easing = patch.easing;
    if (patch.reducedMotion) action.reducedMotion = patch.reducedMotion;
    if (patch.value !== undefined) action.value = patch.value;
    this.dirty();
  }

  private label(items: Array<{ id: string; label: string }>, id: string): string {
    return items.find((item) => item.id === id)?.label ?? id;
  }
  private escape(value: string): string {
    return value.replace(
      /[&<>"']/g,
      (char) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char,
    );
  }
  private showMessage(card: HTMLElement, message: string): void {
    const help = card.querySelector<HTMLElement>('.field-help');
    if (help) help.textContent = message;
  }
}
